import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";

export interface Topic {
  headline: string;
  description: string;
  type: string;
}

export interface TopicsResponse {
  business_name: string;
  business_description: string;
  industry: string;
  icp_guess: string;
  newsletter_goal: string;
  voice_and_tone: string;
  topics: Topic[];
}

export interface Section {
  title: string;
  format: string;
  content_type: string;
  what_it_includes: string;
  content_source: string;
  sample_headline: string;
  how_it_serves_icp: string;
  goal_support: string;
}

export interface SectionsResponse {
  sections: Section[];
  alternative_sections: Section[];
  content_workflow: string;
  content_workflow_description: string;
}

export interface ConfirmResponse {
  session_token: string;
  strategy_summary: {
    newsletter_name: string;
    icp: string;
    template_name: string;
    template_id: number;
    design_direction: string;
  };
}

export interface Template {
  id: number;
  title: string;
  tags: string[];
  preview_html: string | null;
}

export interface StrategySummary {
  newsletter_name: string;
  icp: string;
  newsletter_goal: string;
  content_workflow: string;
  content_workflow_description: string;
  cadence: { frequency: string; day_of_week: string };
  recommended_template_name: string;
  sections: { title: string; format: string; content_type: string }[];
}

export interface StrategyDocResponse {
  google_doc_url: string | null;
  summary: StrategySummary;
}

export interface BuildResponse {
  job_id: string;
}

export interface StatusResponse {
  status: "pending" | "building_blueprint" | "building_edition" | "writing_content" | "generating_images" | "sending_preview" | "completed" | "failed";
  message?: string;
  edition_id?: number;
  blueprint_id?: number;
  magic_link?: string;
  error?: string;
}

class InstantNewsletterService {
  private baseUrl = getApiUrlWithVersion();

  private getAuthHeaders(): Record<string, string> {
    const token = getAuthUser()?.accessToken;
    if (token) {
      return { Authorization: token };
    }
    return {};
  }

  async getTopics(websiteUrl: string): Promise<TopicsResponse> {
    const response = await axios.post(`${this.baseUrl}/instant_newsletters/topics`, {
      website_url: websiteUrl,
    }, { headers: this.getAuthHeaders() });
    return response.data;
  }

  async getSections(websiteUrl: string, selectedTopics: number[]): Promise<SectionsResponse> {
    const response = await axios.post(`${this.baseUrl}/instant_newsletters/sections`, {
      website_url: websiteUrl,
      selected_topics: selectedTopics,
    }, { headers: this.getAuthHeaders() });
    return response.data;
  }

  async confirmSections(websiteUrl: string, confirmedSections: Section[]): Promise<ConfirmResponse> {
    const response = await axios.post(`${this.baseUrl}/instant_newsletters/confirm`, {
      website_url: websiteUrl,
      confirmed_sections: confirmedSections,
    }, { headers: this.getAuthHeaders() });
    return response.data;
  }

  async startBuild(sessionToken: string, templateId?: number, imageStyle?: string, newsletterName?: string, authorName?: string, authorImageUrl?: string): Promise<BuildResponse> {
    const response = await axios.post(`${this.baseUrl}/instant_newsletters/build`, {
      session_token: sessionToken,
      template_id: templateId,
      image_style: imageStyle,
      newsletter_name: newsletterName,
      author_name: authorName,
      author_image_url: authorImageUrl,
    }, { headers: this.getAuthHeaders() });
    return response.data;
  }

  async uploadAuthorImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("image", file);
    const response = await axios.post(`${this.baseUrl}/instant_newsletters/upload_author_image`, formData, {
      headers: { ...this.getAuthHeaders(), "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async sendPreview(sessionToken: string, editionId: number, email: string): Promise<void> {
    await axios.post(`${this.baseUrl}/instant_newsletters/send_preview`, {
      session_token: sessionToken,
      edition_id: editionId,
      email,
    }, { headers: this.getAuthHeaders() });
  }

  async createStrategyDoc(sessionToken: string): Promise<StrategyDocResponse> {
    const response = await axios.post(`${this.baseUrl}/instant_newsletters/strategy_doc`, {
      session_token: sessionToken,
    }, { headers: this.getAuthHeaders() });
    return response.data;
  }

  async getTemplates(): Promise<Template[]> {
    const response = await axios.get(`${this.baseUrl}/instant_newsletters/templates`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async pollStatus(jobId: string): Promise<StatusResponse> {
    const response = await axios.get(`${this.baseUrl}/instant_newsletters/status/${jobId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
}

export default new InstantNewsletterService();
